import { useEffect } from 'react';
import {
	useInfiniteQuery,
	InfiniteData,
	UseInfiniteQueryOptions,
	UseInfiniteQueryResult,
	QueryKey
} from '@tanstack/react-query';
import { useMessageStore } from '../zustandStore/messageStore';
import ApiError from '../lib/ApiError';
import { ApiResultMessages } from './enums';

interface CustomInfiniteQueryOptions<TData, TError, TPageParam> extends UseInfiniteQueryOptions<
	TData,
	TError,
	InfiniteData<TData>,
	QueryKey,
	TPageParam
> {
	showRetryMessageIfError?: boolean;
}

const useCustomInfiniteQuery = <TData = unknown, TError = Error, TPageParam = unknown>(
	options: CustomInfiniteQueryOptions<TData, TError, TPageParam>
): UseInfiniteQueryResult<InfiniteData<TData>, TError> => {
	const updateMessage = useMessageStore((state) => state.updateMessage);

	const queryResult = useInfiniteQuery(
		options as UseInfiniteQueryOptions<TData, TError, InfiniteData<TData>, QueryKey, TPageParam>
	);

	const { error } = queryResult;

	useEffect(() => {
		if (error) {
			const errorName = (error as Error).name;
			const errorMessage = (error as Error).message ?? '';
			if (errorName === 'AbortError' || errorMessage.includes('AbortError')) return;

			if (options.showRetryMessageIfError) updateMessage(ApiResultMessages.GeneralRetryMessage);
			else updateMessage((error as ApiError).body ?? errorMessage ?? 'An error occurred');
		}
	}, [error, updateMessage]);

	return queryResult as unknown as UseInfiniteQueryResult<InfiniteData<TData>, TError>;
};

export default useCustomInfiniteQuery;
